import {
  Typography,
  IconButton,
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  Avatar,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Icon,
  Tooltip,
} from "@mui/material";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import {
  IconBuildingBank,
  IconCircleCheck,
  IconHelpCircle,
  IconX,
} from "@tabler/icons-react";
import { filter, forEach, map, orderBy } from "lodash";
import { useEffect, useState } from "react";
import Corretora from "../../../../models/corretora";
import CorretoraDb from "../../../../db/corretoraDb";
import { onSnapshot, query, where } from "firebase/firestore";
import { radius } from "../../../../theme";

type Props = {
  onClose: (event: {}, reason: "backdropClick" | "escapeKeyDown") => void;
  open: boolean;
};

const NOTA_BMF =
  "https://firebasestorage.googleapis.com/v0/b/felixnabolsa-5c4ce.appspot.com/o/corretoras%2Fbmf.jpg?alt=media&token=b06a0013-41a4-4c23-9ffe-474ce267254c";
const NOTA_BOVESPA =
  "https://firebasestorage.googleapis.com/v0/b/felixnabolsa-5c4ce.appspot.com/o/corretoras%2Fbovespa.jpg?alt=media&token=bfbc10ab-8d55-4fa8-84d7-7957121bdcb9";
export default function PassosCorretora({ onClose, open }: Props) {
  const [corretoras, setCorretoras] = useState<Corretora[]>();
  const [nota, setNota] = useState<string>(NOTA_BOVESPA);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    return onSnapshot(
      query(
        new CorretoraDb().collection({ corretoraId: "" }),
        where("avatar", "!=", null)
      ),
      (snapshot) => {
        const corretorasSnapshot = filter(
          orderBy(
            map(snapshot.docs, (doc) => doc.data()),
            (c) => c.apelido.toLocaleLowerCase()
          ),
          (c) => c.apelido !== "B3"
        );
        setCorretoras(corretorasSnapshot);
      }
    );
  }, []);

  return (
    <Dialog onClose={onClose} open={open} maxWidth="sm" fullWidth>
      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap={2}
        >
          <Typography variant="h3">Corretoras compatíveis:</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Corretora</TableCell>
                <TableCell align="center">Bovespa</TableCell>
                <TableCell align="center">BMF</TableCell>
                <TableCell align="center">Passos</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {map(corretoras, (c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <Box
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      gap={1}
                    >
                      <Avatar
                        src={c.avatar}
                        sx={(theme) => ({
                          height: 32,
                          width: 32,
                          // Fallback do Avatar é cinza neutro do MUI: troca por
                          // ink-08 + tinta secundária (DESIGN.md → anti-padrões).
                          backgroundColor: theme.palette.ink["08"],
                          color: theme.palette.text.secondary,
                        })}
                      >
                        <IconBuildingBank />
                      </Avatar>

                      {/* Cor de marca da corretora vem do banco e não passa pelo
                          tema — o avatar já carrega a identidade, o nome fica em
                          tinta para garantir contraste no light e no dark. */}
                      <Typography variant="h6" color="text.primary">
                        {c.apelido}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {!!c.leitorBovespa ? (
                      <Icon color="success">
                        <IconCircleCheck />
                      </Icon>
                    ) : (
                      <></>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {!!c.leitorBMF ? (
                      <Icon color="success">
                        <IconCircleCheck />
                      </Icon>
                    ) : (
                      <></>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {!!c.passos ? (
                      <Tooltip
                        title={
                          <Box
                            display="flex"
                            flexDirection="column"
                            p={1}
                            gap={1}
                          >
                            {map(c?.passos, (passo, index) => (
                              <PassoFormatado
                                key={index}
                                texto={`${index + 1}. ${passo}`}
                              />
                            ))}
                          </Box>
                        }
                      >
                        <Icon sx={(theme) => ({ color: theme.palette.accentInk })}>
                          <IconHelpCircle />
                        </Icon>
                      </Tooltip>
                    ) : (
                      <></>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box pt={2}>
            <Typography variant="h3">Exemplo de Nota de Corretagem:</Typography>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="center">
            <ToggleButtonGroup
              color="secondary"
              size="small"
              exclusive
              value={nota}
              onChange={(_event, value) => {
                if (!!value) setNota(value);
              }}
            >
              <ToggleButton value={NOTA_BOVESPA} aria-label="bovespa">
                BOVESPA
              </ToggleButton>

              <ToggleButton value={NOTA_BMF} aria-label="bmf">
                BM&F
              </ToggleButton>
            </ToggleButtonGroup>
            <ButtonBase
              onClick={() => setPreviewOpen(true)}
              sx={{ mt: 1.5, borderRadius: `${radius.md}px` }}
            >
              <Box
                component="img"
                src={nota}
                sx={(theme) => ({
                  width: "100%",
                  minHeight: 240,
                  cursor: "zoom-in",
                  borderRadius: `${radius.md}px`,
                  border: `1px solid ${theme.palette.divider}`,
                })}
              />
            </ButtonBase>
          </Box>
        </Box>

        <Dialog
          fullWidth
          maxWidth="lg"
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
        >
          <DialogTitle sx={{ justifyContent: "flex-end" }}>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="flex-end"
              alignItems="center"
            >
              <IconButton
                edge="end"
                aria-label="close"
                size="small"
                onClick={() => setPreviewOpen(false)}
              >
                <IconX />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box
              component="img"
              src={nota}
              sx={(theme) => ({
                width: "100%",
                display: "block",
                borderRadius: `${radius.md}px`,
                border: `1px solid ${theme.palette.divider}`,
              })}
            />
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

function PassoFormatado({ texto }: { texto: string }) {
  let textos: { texto: string; bold: boolean; italic: boolean }[] = [];
  const boldArray = texto.split("*");

  forEach(boldArray, (boldText, boldIndex) => {
    const italicArray = boldText.split("_");
    forEach(italicArray, (italicText, italicIndex) => {
      textos.push({
        texto: italicText,
        bold: boldIndex % 2 === 1,
        italic: italicIndex % 2 === 1,
      });
    });
  });

  return (
    <Box>
      {map(textos, (texto, index) => (
        <Typography
          key={index}
          variant="body2"
          component="span"
          fontStyle={texto.italic ? "italic" : ""}
          fontWeight={texto.bold ? 600 : 400}
        >
          {texto.texto}
        </Typography>
      ))}
    </Box>
  );
}
